'use client';
import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { Skeleton } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { LotteryHeaderContainer } from '../../LotteryHeaderContainer/LotteryHeaderContainer';
import { Select } from '../../form-controls/Select/Select';
import type { LotteryName } from '../../lottery-purchases/types';
import { useCurrentResult } from '../../lottery-results/hooks/useCurrentResult/useCurrentResult';
import type { Drawing, DrawingsResponse } from '../../lottery-results/types';
import { getLotteryDrawFrequency } from '../utils';
import { ResultsSectionLiveDrawLink, ResultsSectionNoResult, ResultsSectionResult, ResultsSectionResultSkeleton } from './components';

type ExplicitResult = {
  result: Pick<Drawing, 'numbers' | 'extraNumbers' | 'extraGames' | 'lotteryId'>;
};

type NeedsFetch = {
  date: Date;
  hideLinkWhenNoResult?: boolean;
  hideStylingWhenNoResult?: boolean;
};

type CommonProps = Omit<ComponentProps<'div'>, 'children'> & {
  drawingId: string;
};

type DecorationsProps = Omit<ComponentProps<typeof LotteryHeaderContainer>, 'lotteryName'> & {
  lotteryName: LotteryName;
};

type ResultsSectionHeaderProps = Omit<ComponentProps<'div'>, 'children'> & {
  drawingDate: string;
  lotteryName: LotteryName;
};

/**
 * ResultsSectionTitle component is responsible for displaying the title of the results section.
 * Optional children can be passed to display additional content next to the title (for example drawing status, like on purchase results section).
 * @param title - The title to display.
 * @param className - (Optional) class name to apply to the component.
 * @param children - (Optional) children to display.
 * @returns The results section title component.
 */
function ResultsSectionTitle({ title, className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div className={twMerge('flex items-center gap-2 justify-between', className)} {...props}>
      <p className="heading-5">{title}</p>
      {children}
    </div>
  );
}

/**
 * ResultSectionSingleResultWithFetch component is responsible for fetching a single result for a given drawing and displaying it.
 * @param drawingId - The ID of the drawing to fetch the result for.
 * @param date - The date of the drawing to fetch the result for.
 * @param hideLinkWhenNoResult - Whether to hide the link when there is no result (used in purchase results section for example).
 * @param hideStylingWhenNoResult - Whether to hide the styling when there is no result (used in purchase results section for example).
 * @param className - (Optional) class name to apply to the component.
 * @returns The results section single result with fetch component.
 */
function ResultSectionSingleResultWithFetch({ drawingId, date, hideLinkWhenNoResult, hideStylingWhenNoResult, ...props }: CommonProps & NeedsFetch) {
  const lotteryId = drawingId.split('_')[0]! as LotteryName;
  const { data, isLoading } = useApiQuery<Drawing>(API_ROUTES.RESULTS.BY_LOTTERY_AND_DRAWING.replace(':lotteryName', lotteryId).replace(':drawingId', drawingId), {
    queryOptions: { placeholderData: previousData => previousData },
  });
  if (isLoading && !data) return <ResultsSectionResultSkeleton className={props.className} />;
  if (!data) return <ResultsSectionNoResult date={date} lottery={lotteryId} hideLink={hideLinkWhenNoResult} hideStyling={hideStylingWhenNoResult} {...props} />;
  const { numbers, extraNumbers, extraGames } = data;
  return <ResultsSectionResult key={drawingId} data={{ numbers, extraNumbers, extraGames, lotteryId }} {...props} />;
}

/**
 * ResultsSectionSingleResult component is responsible for displaying a single result for a given drawing.
 * @param drawingId - The ID of the drawing to display the result for.
 * @param result - The result to display.
 * @param className - (Optional) class name to apply to the component.
 * @returns The results section single result component.
 */
function ResultsSectionSingleResult({ drawingId, result, ...props }: CommonProps & ExplicitResult) {
  return <ResultsSectionResult key={drawingId} data={result} {...props} />;
}

/**
 * ResultsSectionLatestResults component is responsible for displaying the latest results for a given lottery.
 * It fetches the latest results from the API and displays available drawing dates as options in a select component.
 * it also displays the current result as a single result component.
 * @param lotteryName - The name of the lottery to fetch the latest results for.
 * @param className - (Optional) class name to apply to the component.
 * @returns The results section latest results component.
 */
function ResultsSectionLatestResults({ lotteryName }: { lotteryName: LotteryName }) {
  const { data, error } = useApiQuery<DrawingsResponse>(API_ROUTES.LOTTERIES.LOTTERY_RESULTS.replace(':lotteryName', lotteryName));
  const { currentResult, drawingDates, handleResultChange } = useCurrentResult(data ?? [], getLotteryDrawFrequency(lotteryName));

  if (error) return null;
  if (!currentResult) {
    return (
      <>
        <Skeleton className="h-12 w-full rounded-lg" />
        <ResultsSectionResultSkeleton />
      </>
    );
  }

  const { lotteryId, numbers, extraNumbers, extraGames, drawingId, drawingDate } = currentResult;
  return (
    <>
      <Select label="" id="results-section-dropdown" options={drawingDates} value={drawingDate} onChange={handleResultChange} data-track-name="Draw Day" />
      <ResultsSectionSingleResult result={{ lotteryId, numbers, extraNumbers, extraGames }} drawingId={drawingId} />
    </>
  );
}

/**
 * ResultsSection component is responsible for layout of the results section, making sure that spacing between elements is consistent.
 * @param className - (Optional) class name to apply to the component.
 * @returns The results section component.
 */
function ResultsSection({ className, ...props }: ComponentProps<'section'>) {
  return <section className={twMerge('flex flex-col gap-6', className)} data-track-section="Results" {...props} />;
}

function ResultsSectionHeader({ drawingDate, lotteryName, className, ...props }: ResultsSectionHeaderProps) {
  const tDates = useTranslations('lotteries.dates');
  const resolvedDate = new Date(drawingDate);
  const tLottery = useTranslations(`lotteries.${lotteryName}`);

  return (
    <div className={twMerge('flex flex-col gap-2', className)} {...props}>
      <h5>{tLottery('name')}</h5>
      <p>{tDates('results_date', { date: resolvedDate })}</p>
    </div>
  );
}

function ResultsSectionDecorations({ lotteryName, className, ...props }: DecorationsProps) {
  const lotteryHeaderContainerProps = {
    decorationTopRight: `text-${lotteryName.toLocaleLowerCase()}`,
    decorationBottomRight: `text-${lotteryName.toLocaleLowerCase()}`,
    decorationShine: `hidden`,
  };

  const classes = twMerge('rounded-md p-5 gap-4 bg-container-base flex flex-col items-stretch text-foreground', className);

  return <LotteryHeaderContainer className={classes} lotteryName={lotteryName} options={lotteryHeaderContainerProps} data-testid={`results-card-${lotteryName}`} {...props} />;
}

export {
  ResultSectionSingleResultWithFetch,
  ResultsSection,
  ResultsSectionDecorations,
  ResultsSectionHeader,
  ResultsSectionLatestResults,
  ResultsSectionLiveDrawLink,
  ResultsSectionSingleResult,
  ResultsSectionTitle,
};
